import styles from "./list.module.css";
import dayjs from "dayjs";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Tooltip, InputAdornment, IconButton } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { ArrowForwardOutlined, ClearRounded, PrintOutlined } from "@mui/icons-material";
import { Search } from "react-feather";
import { DateRangePicker } from "rsuite";
import { useReactToPrint, ReactToPrint, PrintContextConsumer } from "react-to-print";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { ComponentToPrint } from "./ComponentToPrint";
import { useRef, useCallback } from "react";
import { authData } from "../../../components/GetAuth";

const ButtonArea = ({ data }) => {
    const componentRef = useRef();
    const Export = ({ onExport }) => (
        <Button onClick={(e) => onExport(e.target.value)} className={styles.OutlineBU}>
            Export to CSV
        </Button>
    );

    const convertArrayOfObjectsToCSV = (array) => {
        let result = "";

        const columnDelimiter = ",";
        const lineDelimiter = "\n";
        const keys = ["booking_id", "post_code", "driver_name", "pickup_address", "contact_no", "pickup_date", "assigned_date"];

        keys.forEach((key) => {
            if (key === "booking_id") {
                result += "Booking Id";
            } else if (key === "post_code") {
                result += "Post Code";
            } else if (key === "driver_name") {
                result += "Driver Name";
            } else if (key === "pickup_address") {
                result += "Pickup Address";
            } else if (key === "contact_no") {
                result += "Contact No";
            } else if (key === "pickup_date") {
                result += "Pickup Date";
            } else if (key === "assigned_date") {
                result += "Assigned Date";
            } else {
                result += key;
            }
            result += columnDelimiter;
        });
        result += lineDelimiter;

        array.forEach((item) => {
            let ctr = 0;
            keys.forEach((key) => {
                if (ctr > 0) result += columnDelimiter;
                result += item[key];
                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    };

    const downloadCSV = useCallback(() => {
        let newArray = data.map((row) => {
            return {
                booking_id: row?.booking_id,
                post_code: row?.post_code,
                driver_name: row?.driver?.name,
                pickup_address: row?.pickup_details?.address.replace(/,/g, ""),
                contact_no: row?.pickup_details?.phone,
                pickup_date: dayjs(row.pickup_date).format("DD/MM/YYYY"),
                assigned_date: dayjs(row?.driver?.assigned_date).format("DD/MM/YYYY"),
            };
        });

        const link = document.createElement("a");
        let csv = convertArrayOfObjectsToCSV(newArray);
        if (csv == null) return;

        const filename = "driver-list.csv";

        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", filename);
        link.click();
    }, [data]);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    return (
        <div className={styles.SecondaryActions}>
            <Export onExport={() => downloadCSV()} />
            <Button onClick={handlePrint} className={styles.OutlineBU}>
                Print bookings
            </Button>
            <div style={{ display: "none" }}>
                <ComponentToPrintList ref={componentRef} data={data} />
            </div>
        </div>
    );
};

const ListView = ({ filteredData, filterVars, setSelectedId, setFormOpen, setFilterVars, setViewType, page, setPage, rowsPerPage, setRowsPerPage, totalCount }) => {
    const printRefs = useRef({});
    const auth = authData();

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:assign-driver') > -1;

    return (<>
        {/* Toolbar: search, date filter, view switch — stacks vertically on small screens */}
        <div className={styles.FilterBar}>
            <div className={styles.SearchBox}>
                <Search className={styles.SearchIconNew} />
                <TextField fullWidth placeholder="Search booking ID, customer, postcode..." value={filterVars?.searchKey} onChange={(e) => setFilterVars((prev) => { return { ...prev, searchKey: e.target.value }; })}
                    InputProps={{
                        endAdornment: filterVars.searchKey && (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label="Clear search" onClick={(e) => { setFilterVars((prev) => { return { ...prev, searchKey: '' }; }); }}>
                                    <ClearRounded fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <div className={styles.DateFilter}>
                <DateRangePicker
                    format={"dd/MM/yyyy"}
                    placement={"bottomEnd"}
                    placeholder="Search by date"
                    value={filterVars?.date}
                    showOneCalendar
                    onChange={(e) => setFilterVars((prev) => ({ ...prev, date: e }))}
                />
            </div>
        </div>

        <ButtonArea data={filteredData} />

        {/* Desktop / tablet table — hidden on narrow screens in favour of the card list below */}
        <div className={styles.TableScrollWrap}>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Post Code</StyledMainTableCell>
                            <StyledMainTableCell>Driver Name</StyledMainTableCell>
                            <StyledMainTableCell>Pickup Address</StyledMainTableCell>
                            <StyledMainTableCell>Contact No</StyledMainTableCell>
                            <StyledMainTableCell>Pickup Date</StyledMainTableCell>
                            <StyledMainTableCell>Assigned Date</StyledMainTableCell>
                            <StyledMainTableCell>Action</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.map((item) => {
                            return (<StyledMainTableRow key={item?.id}>
                                <TableCell component="th" scope="row"><span className={styles.MonoCellStrong}>{item?.booking_id}</span></TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{item?.post_code}</span></TableCell>
                                <TableCell component="th" scope="row">{item?.driver?.name}</TableCell>
                                <TableCell component="th" scope="row">{item?.pickup_details?.address}</TableCell>
                                <TableCell component="th" scope="row">{item?.pickup_details?.phone}</TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</span></TableCell>
                                <TableCell component="th" scope="row"><span className={styles.MonoCell}>{dayjs(item?.driver?.assigned_date).format("DD/MM/YYYY")}</span></TableCell>
                                <TableCell component="th" scope="row">
                                    <div className={styles.RowActions}>
                                        <div style={{ display: "none" }}>
                                            <ComponentToPrint ref={(el) => (printRefs.current[item?.id] = el)} bookingData={filteredData} driver_id={item?.driver?.id} />
                                        </div>
                                        <ReactToPrint content={() => printRefs.current[item?.id]}>
                                            <PrintContextConsumer>
                                                {({ handlePrint }) => (<Tooltip title="Print"><Button className={styles.ActionBU} onClick={handlePrint}><PrintOutlined /></Button></Tooltip>)}
                                            </PrintContextConsumer>
                                        </ReactToPrint>
                                        {canAssign && <Tooltip title="Re-Assign or Unassign">
                                            <Button className={styles.ActionBU} onClick={(e) => { setFormOpen(true); setSelectedId(item?.id); }}><ArrowForwardOutlined /></Button>
                                        </Tooltip>}
                                    </div>
                                </TableCell>
                            </StyledMainTableRow>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">No data found</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={totalCount}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>

        {/* Mobile card list — same data, shown only on small screens (see @media 768px in list.module.css) */}
        <div className={styles.CardList}>
            {filteredData.map((item) => (
                <div className={styles.DataCard} key={item?.id}>
                    <div className={styles.DataCardTop}>
                        <span className={styles.DataCardId}>{item?.booking_id}</span>
                        <span className={styles.DataCardPostCode}>{item?.post_code}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Driver</span>
                        <span className={styles.DataCardValue}>{item?.driver?.name || "—"}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Pickup address</span>
                        <span className={styles.DataCardValue}>{item?.pickup_details?.address}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Contact</span>
                        <span className={styles.DataCardValue}>{item?.pickup_details?.phone}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Pickup date</span>
                        <span className={styles.DataCardValue}>{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</span>
                    </div>
                    <div className={styles.DataCardRow}>
                        <span className={styles.DataCardLabel}>Assigned date</span>
                        <span className={styles.DataCardValue}>{dayjs(item?.driver?.assigned_date).format("DD/MM/YYYY")}</span>
                    </div>

                    <div className={styles.DataCardActions}>
                        <div style={{ display: "none" }}>
                            <ComponentToPrint ref={(el) => (printRefs.current[`m-${item?.id}`] = el)} bookingData={filteredData} driver_id={item?.driver?.id} />
                        </div>
                        <ReactToPrint content={() => printRefs.current[`m-${item?.id}`]}>
                            <PrintContextConsumer>
                                {({ handlePrint }) => (<Button className={styles.ActionBU} onClick={handlePrint}><PrintOutlined /></Button>)}
                            </PrintContextConsumer>
                        </ReactToPrint>
                        {canAssign && (
                            <Button className={styles.ActionBU} onClick={(e) => { setFormOpen(true); setSelectedId(item?.id); }}><ArrowForwardOutlined /></Button>
                        )}
                    </div>
                </div>
            ))}

            {filteredData.length === 0 && (
                <div className={styles.EmptyState}>No data found</div>
            )}

            {filteredData?.length > 0 && (
                <TablePaginationComponent
                    count={totalCount}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            )}
        </div>
    </>);
};

export default ListView;